import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  type MediaDataRoomInsightsFormValues,
  useSelectedAudienceAggregations,
} from "features/mediaDataRoom/components";
import { useMediaDataRoomOverlapInsights } from "features/mediaDataRoom/contexts";
import { type MediaDataRoomVersion } from "features/mediaDataRoom/models";
import { getMediaDataRoomVersionedValue } from "features/mediaDataRoom/utils";

const getOverlapColumnToPresentationMapForVersion = (
  version: MediaDataRoomVersion
) =>
  new Map<string, string>([
    ["affinity_ratio", "Affinity"],
    ["share_in_overlap", "Share in overlap"],
    [
      "share_in_matchable_audience",
      getMediaDataRoomVersionedValue(version, {
        ab: "Share in matchable publisher audience",
        v3: "Share in matchable base audience",
      }),
    ],
    [
      "addressable_audience_size",
      getMediaDataRoomVersionedValue(version, {
        ab: "Addressable users in publisher audience",
        v3: "Addressable users in base audience",
      }),
    ],
    ["count_users_overlap", "Count in overlap"],
  ]);

const useVisualizeSelectorOrderByData = () => {
  const { version } = useMediaDataRoomOverlapInsights();
  const { watch } = useFormContext<MediaDataRoomInsightsFormValues>();
  const [visualizeSelectorData, setVisualizeSelectorData] = useState<
    { label: string; value: string }[]
  >([]);
  const [rowsTotalCount, setRowsTotalCount] = useState<number>(0);
  const selectedAudienceType = watch("audienceType");
  const selectedAggregation = watch("aggregation");
  const aggregations = useSelectedAudienceAggregations(selectedAudienceType);
  useEffect(() => {
    const {
      columns = [],
      aggregation = [],
      rows = [],
    } = aggregations.find(({ id }) => id === selectedAggregation) || {};

    const orderByFieldsData = columns
      ?.slice(aggregation.length)
      ?.filter((column) => column !== "count_users_overlap")
      ?.map((column) => {
        return {
          label:
            getOverlapColumnToPresentationMapForVersion(version).get(column) ||
            column,
          value: column,
        };
      });
    setVisualizeSelectorData(orderByFieldsData);
    setRowsTotalCount(rows?.length || 0);
  }, [aggregations, selectedAggregation, selectedAudienceType, version]);

  return { rowsTotalCount, visualizeSelectorData };
};

export default useVisualizeSelectorOrderByData;
